/**
 * @file useFramePreview.ts
 * @description Предпросмотр «не чаще кадра» для общих полей конструктора
 * (phase19.16, партия SB120 «свобода выбора», этап 0; site-builder.md SB120;
 * проект docs/phases/phase19.16-materials/uiux-sb120-fields.md §2 «Канва:
 * превью не чаще кадра, запись в черновик по отпусканию»).
 *
 * `schedule(v)` копит последнее значение и отдаёт его в `onPreview` один раз
 * за кадр (`requestAnimationFrame`); `cancel()` снимает недоставленное —
 * его зовут перед записью, чтобы запоздалый кадр не перекрыл сохранённое.
 */

import { useCallback, useEffect, useRef } from "react";

export function useFramePreview<T>(onPreview: ((value: T) => void) | undefined) {
  const frame = useRef<number | null>(null);
  const pending = useRef<{ value: T } | null>(null);
  // Свежий обработчик без перезапуска кадров при каждом рендере родителя.
  const handler = useRef(onPreview);
  useEffect(() => {
    handler.current = onPreview;
  }, [onPreview]);

  const cancel = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    pending.current = null;
  }, []);

  const schedule = useCallback((value: T) => {
    if (!handler.current) return;
    pending.current = { value };
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      const next = pending.current;
      pending.current = null;
      if (next) handler.current?.(next.value);
    });
  }, []);

  /** Сразу, без кадра: откат предпросмотра (Esc, нечисло). */
  const now = useCallback(
    (value: T) => {
      cancel();
      handler.current?.(value);
    },
    [cancel],
  );

  // Уходим со страницы посреди перетаскивания — кадр не должен прийти в пустоту.
  useEffect(() => cancel, [cancel]);

  return { schedule, cancel, now };
}
